/**
 * Gallery axis strategy for 'index' mode.
 *
 * Displays one row per media index, with step and run controlled via sliders.
 */

import {resolveCurrentStep} from '../../utils/stepUtils';
import {type GridRow, type SlotRegistry, type TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {caption, compareEntry, label, stepSlider} from '../utils/slots';
import {createEmptySlotRegistry} from '../utils/slotUtils';
import {type GalleryAxisStrategy} from './GalleryAxisStrategy';

export class IndexAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {stepMeta} = input;

    const indices = stepMeta.indices;

    // Apply maxGalleryItems limit to index range
    const indicesToProcess =
      maxItems && indices.length > maxItems
        ? indices.slice(0, maxItems)
        : indices;

    return indicesToProcess;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, config, allMasks, mediaType} = input;
    const {mediaKeys} = config;

    if (!mediaKeys || mediaKeys.length === 0 || !mediaType) {
      return [];
    }

    // Use first run for index axis
    const run = runs[0];
    if (!run) {
      return [];
    }

    const runStepMeta = stepMeta.runs[0];
    if (!runStepMeta) {
      return [];
    }

    const currentStep = resolveCurrentStep(
      config.stepIndex,
      stepMeta,
      config.mediaSliderKey
    );

    const rows: GridRow[] = [];

    for (const idx of axisValues) {
      const cells = createCellsForMediaKeys(
        run,
        runStepMeta,
        currentStep,
        idx,
        mediaKeys,
        config,
        allMasks,
        mediaType
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `index-${idx}`,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(
    stepMeta?: TransformInput['stepMeta'],
    isSingleRunView?: boolean
  ): SlotRegistry {
    // Index axis: indices shown on each tile, step and run are controlled
    const slots = createEmptySlotRegistry();
    slots.tileHeader = [label('index')];
    slots.tileFooter = [caption()];
    slots.contentHeader = [
      {
        control: 'pagination',
        type: 'component',
      },
    ];

    const hasStepSlider = stepMeta?.steps && stepMeta?.steps.length > 1;
    if (hasStepSlider) {
      slots.contentFooter!.push(stepSlider());
    }

    return slots;
  }
}
